<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useFetch } from "@vueuse/core";
import { gql } from "@urql/core";
import { useQuery } from "@urql/vue";
import { IconUpload } from "@tabler/icons-vue";
import { token } from "@/init";
import FormDialog from "@/components/common/FormDialog.vue";

const props = withDefaults(
  defineProps<{
    templateId: string;
    language: string;
    highlighted?: boolean;
    rowClass?: string;
    classicTheme?: boolean;
  }>(),
  { highlighted: false, rowClass: "" },
);

const emit = defineEmits<{
  visibilityChange: [visible: boolean];
  mouseenter: [];
  mouseleave: [];
}>();

type PreviewConfiguration = {
  max_file_bytes: number;
  copy: {
    section: string;
    title: string;
    formats: string;
    preview_title: string;
    preview_description: string;
  };
};
const previewUrl = computed(
  () =>
    `/2/hcp_uploads/preview_configuration?locale=${encodeURIComponent(props.language?.toLowerCase().split("-")[0] || "en")}`,
);
const {
  data: previewConfiguration,
  isFetching,
  error,
} = useFetch(previewUrl, {
  refetch: true,
}).json<PreviewConfiguration>();
const previewCopy = computed(() =>
  !isFetching.value && !error.value
    ? previewConfiguration.value?.copy
    : undefined,
);

const queryVariables = computed(() => ({ id: props.templateId }));

const UploadPreviewConfigDocument = gql`
  query HcpUploadTemplatePreviewConfig($id: BigInt!) {
    documentSpaceConfig(id: $id) {
      id
      uploadsEnabled
    }
  }
`;

const uploadPreviewConfigQuery = useQuery<{
  documentSpaceConfig?: { id: string; uploadsEnabled: boolean } | null;
}>({
  query: UploadPreviewConfigDocument,
  variables: queryVariables,
  requestPolicy: "cache-and-network",
  pause: computed(() => !token.value),
});

const uploadsEnabled = computed(
  () =>
    !uploadPreviewConfigQuery.fetching.value &&
    uploadPreviewConfigQuery.data.value?.documentSpaceConfig?.id ===
      props.templateId &&
    (uploadPreviewConfigQuery.data.value?.documentSpaceConfig?.uploadsEnabled ??
      false),
);

const visible = computed(
  () => uploadsEnabled.value && Boolean(previewCopy.value),
);
watch(visible, (value) => emit("visibilityChange", value), { immediate: true });

const previewDialogOpen = ref(false);

watch(
  [visible, () => props.templateId, () => props.language],
  ([visible, templateId, language], previous) => {
    if (
      !visible ||
      (previous && (previous[1] !== templateId || previous[2] !== language))
    ) {
      previewDialogOpen.value = false;
    }
  },
);

function openPreviewDialog() {
  previewDialogOpen.value = true;
}
</script>

<template>
  <section
    v-if="visible && previewCopy"
    class="hcp-upload-preview-container"
    :class="rowClass"
    @mouseenter="emit('mouseenter')"
    @mouseleave="emit('mouseleave')"
  >
    <div class="hcp-upload-preview-section">{{ previewCopy.section }}</div>
    <div data-test="hcp-upload-preview-row">
      <button
        data-test="hcp-upload-preview-button"
        type="button"
        class="hcp-upload-preview-button"
        :class="{ 'hcp-upload-preview-row--highlighted': highlighted }"
        aria-haspopup="dialog"
        @click="openPreviewDialog"
        @keydown.enter.prevent="openPreviewDialog"
        @keydown.space.prevent="openPreviewDialog"
      >
        <IconUpload class="hcp-upload-preview-icon" :stroke-width="1.7" />
        <span class="hcp-upload-preview-copy">
          <strong>{{ previewCopy.title }}</strong>
          <small>{{ previewCopy.formats }}</small>
        </span>
      </button>
    </div>
  </section>
  <FormDialog
    v-if="previewCopy"
    :classic-theme="classicTheme"
    v-model="previewDialogOpen"
    :title="previewCopy.preview_title"
    :message="previewCopy.preview_description"
    submit-label="OK"
    hide-cancel
    @submit="previewDialogOpen = false"
  />
</template>

<style scoped>
.hcp-upload-preview-container {
  position: relative;
  margin-bottom: 1rem;
}
.hcp-upload-preview-section {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: 600;
  margin: 0.8em 0 1.2em;
}
.hcp-upload-preview-section::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #b5b5b5;
}
.hcp-upload-preview-button {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  min-height: 44px;
  padding: 1rem;
  text-align: left;
  border: 1px solid #a6cccc;
  border-radius: 6px;
  background: #f4fafa;
  color: #285b60;
  font: inherit;
  cursor: pointer;
}
.hcp-upload-preview-button:hover {
  background: #e9f5f4;
}
.hcp-upload-preview-button:focus-visible {
  outline: 2px solid #285b60;
  outline-offset: 2px;
}
.hcp-upload-preview-icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
}
.hcp-upload-preview-copy {
  flex: 1 1 8rem;
  min-width: 0;
  overflow-wrap: anywhere;
}
.hcp-upload-preview-copy strong,
.hcp-upload-preview-copy small {
  display: block;
}
.hcp-upload-preview-copy small {
  margin-top: 0.25rem;
  font-size: 0.75em;
}
.hcp-upload-preview-row--highlighted {
  box-shadow: inset 0 0 0 3px #00beb8;
}
</style>
